import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  input,
  output,
} from '@angular/core';
import { tap, BehaviorSubject, Subscription, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ITablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { ITableColumn } from 'src/app/models/tableColumns';
import { aiInLineControlTableColumns } from 'src/app/models/tableColumns/aiInLineControlTableColumn';
import { ActiveFiltersHeightService } from 'src/app/services/infra/activeFiltersHeight/active-filters-height.service';
import { InLineControlModel } from 'src/app/models/api/inLineControl.model';
import { InLineControlTableRowViewModel } from 'src/app/models/viewModels/inLineControlTableRowViewModel';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { getAiServiceLogoPath } from 'src/app/common/aiServiceUtils';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { JsonHalCollection, JsonHalCollectionPageInfo, JsonHalLinks } from 'src/app/models/api/jsonhal.model';
import { Stack } from 'src/app/common/dataStructures/stack';
import { IPaginationNavigationModel } from 'src/app/models/paginationNavigation.model';
import { PaginationOperationEnum } from 'src/app/components/table/table.component';
import { AiService } from 'src/app/models/api/agentStep.model';
import { SearchActivityHttpService } from 'src/app/services/httpServices/detection/searchActivityService/search-activity-http.service';
import { formatFromDateWithSeconds } from 'src/app/common/datetime_utility';
import { SidePanelService } from 'src/app/features/side-panel/services/side-panel.service';
import { QueryParams } from 'src/app/models/navigation/navigationParams.interface';
import { InLineControlFiltersModel } from 'src/app/models/filters/implementations/pageFilters/inLineControlFilters.model';
import { FiltersService } from 'src/app/services/filters/filters.service';
import { PageWithFiltersEnum } from 'src/app/models/filters/pageWithFilters.enum';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';

@Component({
  selector: 'app-enterprise-ai-in-line-control',
  templateUrl: './app-enterprise-ai-in-line-control.component.html',
  styleUrl: './app-enterprise-ai-in-line-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppEnterpriseAiInLineControlComponent implements OnInit, OnDestroy {
  public integration = input.required<string>();
  public readonly totalItems = output<number>();

  public pageFilters = signal<InLineControlFiltersModel>(
    this._filtersService.getFilters(PageWithFiltersEnum.InLineControl) as InLineControlFiltersModel
  );
  public columns = signal<ITableColumn[]>(aiInLineControlTableColumns);
  public isLoading = signal<boolean>(true);

  public inLineControlCurrentPage$ = new BehaviorSubject<InLineControlTableRowViewModel[]>([]);
  public sortedColumn$ = new BehaviorSubject<SortColumn | undefined>(undefined);
  public pageInfo$: BehaviorSubject<ITablePageInfo>;

  public activeTabIndex = 0;
  public showNoData = false;
  public inLineControlData: InLineControlModel[] = [];
  public readonly defaultPageSize: number;
  private _paginationNavigation: IPaginationNavigationModel;
  private _sortOnChangeSubscription$: Subscription;
  private _firstPageSubscription$: Subscription;
  private _fetchByPageSubscription$: Subscription;
  private _filtersSubscription$: Subscription;
  private readonly _notAvailableString: string;
  private readonly _httpClient = inject(HttpClient);
  private readonly _featureFlagsService = inject(FeatureFlagsService);
  private _iamDemoMockInlineData: InLineControlModel[] | null = null;
  private _isOnFirstPage = true;

  constructor(
    private readonly _renderer: Renderer2,
    private readonly _el: ElementRef,
    private readonly _destroyRef: DestroyRef,
    private readonly _activeFiltersHeightService: ActiveFiltersHeightService,
    private readonly _searchActivityHttpService: SearchActivityHttpService,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _logger: LoggerService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _sidePanel: SidePanelService,
    private readonly _filtersService: FiltersService,
    private readonly _activatedRoute: ActivatedRoute
  ) {
    this.defaultPageSize = this._searchActivityHttpService.defaultPageSize;
    this._notAvailableString = this._labelsProviderPipe.transform('common.notAvailable');
    this._initializePageInfo();
  }

  ngOnInit(): void {
    this._activeFiltersHeightService.height$
      .pipe(
        tap((height: number) =>
          this._renderer.setStyle(this._el.nativeElement, '--applied-filters-height', `${height}px`, 1)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();

    // Set default sort by timestamp in descending order
    const timestampColumn = this.columns().find((col) => col.sortKey === 'timestamp');

    if (!timestampColumn) {
      throw new Error('Timestamp column not found');
    }
    const defaultSort: SortColumn = {
      column: timestampColumn,
      isDesc: true,
    };
    this.sortedColumn$.next(defaultSort);

    this._subscribeToSortOnChange();

    const filters = this.pageFilters();

    if (filters) {
      const { onUpdate$ } = filters;

      this._filtersSubscription$ = onUpdate$.subscribe(() => {
        this._fetchFirstPage();
      });
    }

    this.fetchInLineControlData();

    // Only close the agentStep panel if there's no
    // inLineControl panel to open from URL.
    // closePanel sets _lastCloseTime which blocks the
    // query-param subscription within 300ms.
    const hasInLineControl = this._activatedRoute.snapshot.queryParamMap.has(QueryParams.inLineControl);
    if (!hasInLineControl) {
      this._sidePanel.closePanel([QueryParams.agentStep]);
    }
  }

  ngOnDestroy(): void {
    if (this._sortOnChangeSubscription$) {
      this._sortOnChangeSubscription$.unsubscribe();
    }
    if (this._firstPageSubscription$) {
      this._firstPageSubscription$.unsubscribe();
    }
    if (this._fetchByPageSubscription$) {
      this._fetchByPageSubscription$.unsubscribe();
    }
    if (this._filtersSubscription$) {
      this._filtersSubscription$.unsubscribe();
    }
  }

  public fetchInLineControlData(): void {
    this._fetchFirstPage();
  }

  public onColumnSortClick(sortData: SortColumn): void {
    this.sortedColumn$.next(sortData);
  }

  public onPagination(operation: PaginationOperationEnum) {
    if (!this.isLoading()) {
      if (operation == PaginationOperationEnum.next) {
        this._paginationNavigation.previousPage.push(this._paginationNavigation.currentPage);
      }

      const pageUrl =
        operation == PaginationOperationEnum.next
          ? this._paginationNavigation.nextPage
          : this._paginationNavigation.previousPage.pop();

      this._isOnFirstPage =
        operation !== PaginationOperationEnum.next &&
        this._paginationNavigation.previousPage.size() === 0;

      if (pageUrl) {
        this._doPaginationWhenTokenExist(pageUrl);
      }
    }
  }

  private _subscribeToSortOnChange() {
    this._sortOnChangeSubscription$ = this.sortedColumn$.subscribe(() => {
      this._fetchFirstPage();
    });
  }

  private _fetchFirstPage() {
    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();
    this._isOnFirstPage = true;

    const currentSort = this.sortedColumn$.value;
    const currentFilters = this.pageFilters()?.getSelectedFilters();
    this._firstPageSubscription$ = this._searchActivityHttpService
      .startGetFindings(currentSort, currentFilters)
      .subscribe({
        next: (response: JsonHalCollection<InLineControlModel>) => {
          this._handleInLineControlCollectionResponse(response);
        },
        error: (err: HttpErrorResponse) => this._fetchRequestOnError(err),
      });
  }

  private _fetchByPage(continuationToken: string) {
    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();

    const currentFilters = this.pageFilters()?.getSelectedFilters();
    this._fetchByPageSubscription$ = this._searchActivityHttpService
      .getFindingsByContinuationToken(continuationToken, currentFilters)
      .subscribe(
        (response) => this._handleInLineControlCollectionResponse(response),
        (error) => this._fetchRequestOnError(error)
      );
  }

  private _handleInLineControlCollectionResponse(
    response: JsonHalCollection<InLineControlModel>
  ) {
    this._handlePaginationUrls(response._links);
    this._handlePageInfo(response.page);

    const items = response._embedded.items;
    let viewModels = items?.length
      ? items.map((item) => this._transformToViewModel(item))
      : [];

    if (this._isIamDemoEnabled()) {
      this._loadIamDemoInlineMock(viewModels);
    } else {
      this._setInlineControlPage(viewModels);
    }
  }

  private _setInlineControlPage(
    viewModels: InLineControlTableRowViewModel[]
  ): void {
    const currentSort = this.sortedColumn$.value;
    if (currentSort) {
      viewModels = this._sortViewModels(viewModels, currentSort);
    }
    this.showNoData = viewModels.length === 0;
    this.inLineControlCurrentPage$.next(viewModels);
    this.isLoading.set(false);
  }

  private _isIamDemoEnabled(): boolean {
    return this._featureFlagsService.isFeatureEnabled(
      FeatureFlagTypes.SpmIamDemoAmex
    );
  }

  private _loadIamDemoInlineMock(
    apiViewModels: InLineControlTableRowViewModel[]
  ): void {
    if (!this._isOnFirstPage || !this._isIamDemoEnabled()) {
      this._setInlineControlPage(apiViewModels);
      return;
    }

    if (this._iamDemoMockInlineData) {
      this._prependIamDemoInlineMock(apiViewModels);
      return;
    }

    this._httpClient
      .get<InLineControlModel[]>(
        'assets/mockData/iamDemoInLineControlMockData.json'
      )
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (data) => {
          this._iamDemoMockInlineData =
            this._applyDynamicInlineTimestamps(data);
          this._prependIamDemoInlineMock(apiViewModels);
        },
        error: () => this._setInlineControlPage(apiViewModels),
      });
  }

  private _applyDynamicInlineTimestamps(
    models: InLineControlModel[]
  ): InLineControlModel[] {
    const now = new Date();
    const base = new Date(now);
    base.setMinutes(now.getMinutes() - 5);

    return models.map((m, i) => {
      const offset = i * 15 * 1000;
      const ts = new Date(base.getTime() - offset);
      return { ...m, timestamp: ts.toISOString() };
    });
  }

  private _prependIamDemoInlineMock(
    apiViewModels: InLineControlTableRowViewModel[]
  ): void {
    const mockVms = (this._iamDemoMockInlineData ?? []).map((m) =>
      this._transformToViewModel(m)
    );
    const combined = [...mockVms, ...apiViewModels];
    this._setInlineControlPage(combined);
  }

  private _sortViewModels(
    viewModels: InLineControlTableRowViewModel[],
    sortData: SortColumn
  ): InLineControlTableRowViewModel[] {
    return [...viewModels].sort((a, b) => {
      const aValue = a[sortData.column.sortKey as keyof InLineControlTableRowViewModel];
      const bValue = b[sortData.column.sortKey as keyof InLineControlTableRowViewModel];

      if (aValue === undefined || bValue === undefined) return 0;

      // Handle timestamp sorting
      if (sortData.column.sortKey === 'timestamp') {
        const aDate = new Date(aValue as string).getTime();
        const bDate = new Date(bValue as string).getTime();
        return sortData.isDesc ? bDate - aDate : aDate - bDate;
      }

      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortData.isDesc ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
      }

      // Handle number sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortData.isDesc ? bValue - aValue : aValue - bValue;
      }

      return 0;
    });
  }

  public rowSelected(selection: InLineControlTableRowViewModel): void {
    this._sidePanel.openInLineControlDetails(selection);
  }

  public rowUnselected = () => this._sidePanel.close();

  private _cancelOngoingHttpRequest() {
    if (this._firstPageSubscription$) {
      this._firstPageSubscription$.unsubscribe();
    }

    if (this._fetchByPageSubscription$) {
      this._fetchByPageSubscription$.unsubscribe();
    }
  }

  private _fetchRequestOnError(error: HttpErrorResponse) {
    this.inLineControlCurrentPage$.next([]);
    this.isLoading.set(false);
    this.showNoData = true;
    this._httpErrorHandlerService.handle(error);
  }

  private _tableBehaviorChangeBeforeFetch() {
    this.inLineControlCurrentPage$.next([]);
    this.isLoading.set(true);
  }

  private _handlePaginationUrls(links: JsonHalLinks) {
    if (!this._paginationNavigation) {
      this._paginationNavigation = {
        currentPage: links.self.href,
        nextPage: links.next?.href,
        previousPage: new Stack<string>(),
      } as IPaginationNavigationModel;
    } else {
      this._paginationNavigation.currentPage = links.self.href;
      this._paginationNavigation.nextPage = links.next?.href;
    }
  }

  private _handlePageInfo(pageInfo: JsonHalCollectionPageInfo) {
    this.totalItems.emit(pageInfo.totalItems);
    const tablePageInfo = {
      number: pageInfo.number ?? 1,
      currSize: pageInfo.size,
      maxSize: this.defaultPageSize,
      totalItems: pageInfo.totalItems,
      totalPages: pageInfo.totalPages,
    };

    if (!this.pageInfo$) {
      this.pageInfo$ = new BehaviorSubject(tablePageInfo);
    } else {
      this.pageInfo$.next(tablePageInfo);
    }
  }

  private _doPaginationWhenTokenExist(url: string) {
    const continuationToken = this._tokenExtractor(url);

    if (continuationToken) {
      this._fetchByPage(continuationToken);
    } else {
      this._fetchFirstPage();
    }
  }

  private _tokenExtractor(url: string): string | null {
    const continuationToken = new URLSearchParams(url.slice(url.indexOf('?'))).get('continuationToken');
    if (!continuationToken) {
      this._logger.error('Failed to extract continuation token from json hal next activity resource page link');
      return null;
    }

    return continuationToken;
  }

  private _getAiService(row: InLineControlModel): AiService {
    let app = (row.relatedSteps?.[0]?.context?.agent?.provider?.name || '').toLowerCase();
    app = app.indexOf('github') === -1 ? app : 'github';

    switch (app) {
      case 'custom agent':
        return AiService.customAgent;
      case 'github':
        return AiService.gitHub;
      case 'slack':
        return AiService.slack;
      case 'copilot app':
      case 'm365copilot':
      case 'cps':
      case 'copilot':
        return AiService.m365Copilot;
      case 'azure foundry':
        return AiService.azureAiFoundry;
      case 'chatgpt':
        return AiService.chatGptEnterprise;
      case 'claude desktop':
        return AiService.claude;
      case 'copilot studio':
        return AiService.copilotStudio;
      case 'perplexity':
        return AiService.perplexity;
      case 'agentkit':
        return AiService.agentKit;
      case 'cursor':
        return AiService.cursor;
      default:
        return AiService.m365Copilot;
    }
  }

  private _transformToViewModel(row: InLineControlModel): InLineControlTableRowViewModel {
    const aiService = this._getAiService(row);
    return {
      rowId: row?.id ?? '',
      id: row?.id ?? '',
      integrationId: row?.integrationId ?? '',
      ruleName: row?.rule?.name ?? '',
      ruleId: row?.rule?.id ?? '',
      preventionType: row?.rule?.action === 'block' ? 'Blocked' : 'Detected',
      preventionTypeIcon: this._getPreventionIcon(row?.rule?.action ?? ''),
      timestamp: row?.timestamp ? formatFromDateWithSeconds(new Date(row.timestamp)) : '',
      agent: this._getInLineControlAgentName(row),
      actor: row?.mainActor?.id || undefined,
      threadId: row?.threadId ?? '',
      agentId: row?.relatedSteps?.[0]?.context?.agent?.id || undefined,
      clickable: false,
      aiServiceIcon: getAiServiceLogoPath(aiService),
      aiService: aiService,
      ruleSummary: row?.rule?.summary ?? '',
      // API provides evidence at root level (row.evidence) for both taint analysis and findings.
      // Both fields use the same source; the details component renders them differently based on isTaintAnalysis().
      taintAnalysisEvidence: row?.evidence,
      findingsEvidence: row?.evidence,
      environmentId:
        (row?.relatedSteps?.[0]?.context as { additionalProperties?: { environmentId?: string } })?.additionalProperties
          ?.environmentId ?? '',
    };
  }

  private _getInLineControlAgentName(row: InLineControlModel): string {
    const agentName = row?.relatedSteps?.[0]?.context?.agent?.name;

    // Only return agent name if it actually exists and is not empty
    if (agentName && agentName.trim() !== '') {
      return agentName;
    }

    // Return empty string if no real agent name is available
    // This allows the UI to show agent ID instead
    return '';
  }

  private _getPreventionIcon(preventionType: string): string {
    switch (preventionType) {
      case 'block':
        return 'assets/images/dataflow-icons/blocked.svg';
      case 'detect':
        return 'assets/images/dataflow-icons/shield-alert.svg';
      default:
        return 'assets/images/dataflow-icons/shield-alert.svg';
    }
  }

  private _initializePageInfo() {
    this.pageInfo$ = new BehaviorSubject({
      number: 1,
      currSize: 0,
      maxSize: this._searchActivityHttpService.defaultPageSize,
      totalItems: 0,
      totalPages: 1,
    });
  }
}
